import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode,
} from "react";
import { makeStyles } from "@fluentui/react-components";
import { generateNotificationId } from "../utils/notification";

/* global setTimeout */

const useStyles = makeStyles({
  container: {
    position: "fixed",
    top: "16px",
    right: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    zIndex: "1000",
  },
  notification: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    width: "100%",
    height: "70px",
    borderRadius: "8px",
    padding: "3px 8px",
  },
  info: {
    backgroundColor: "#3d4750",
    "& .durationIndicator": {
      backgroundColor: "rgba(44, 41, 50, 0.9)",
    },
  },
  success: {
    backgroundColor: "#1A7528",
    "& .durationIndicator": {
      backgroundColor: "rgba(44, 41, 50, 0.25)",
    },
  },
  warning: {
    backgroundColor: "#f6d150",
    color: "#202b36",
    "& .content": {
      color: "#202b36",
    },
    "& .durationIndicator": {
      backgroundColor: "rgba(44, 41, 50, 0.12)",
    },
  },
  error: {
    backgroundColor: "#C62906",
    "& .durationIndicator": {
      backgroundColor: "rgba(44, 41, 50, 0.25)",
    },
  },
  content: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#fff",
    width: "100%",
    height: "100%",
    "& p": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      "-webkit-line-clamp": "2",
      "-webkit-box-orient": "vertical",
      padding: "0 16px",
    },
  },
  durationIndicatorContainer: {
    position: "relative",
    width: "100%",
    padding: "0 8px",
  },
  durationIndicator: {
    position: "absolute",
    left: "0",
    right: "0",
    bottom: "5px",
    height: "6px",
    borderRadius: "14px",
    animationName: "grow",
    animationTimingFunction: "linear",
  },
  "@keyframes grow": {
    "&from": {
      width: "0",
    },
    "&to": {
      width: "100%",
    },
  },
});

interface NotificationContextType {
  addNotification: (params: NotificationParams) => void;
}

interface NotificationParams {
  message: string;
  type?: "success" | "error" | "warning" | "info";
  duration?: number;
}

interface Notification extends NotificationParams {
  id: string;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const styles = useStyles();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((params: NotificationParams) => {
    const id = generateNotificationId();
    const newNotification: Notification = {
      id,
      ...params,
      duration: params.duration || 5000,
    };

    setNotifications((prevNotifications) => [
      ...prevNotifications,
      newNotification,
    ]);

    if (params.duration !== 0) {
      setTimeout(() => {
        removeNotification(id);
      }, params.duration || 5000);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((notification) => notification.id !== id),
    );
  }, []);

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      <div className={styles.container}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`${styles.notification} ${styles[notification.type || "info"]}`}
          >
            <div className={styles.content}>
              <p>{notification.message}</p>
            </div>
            <div className={styles.durationIndicatorContainer}>
              <div
                className={styles.durationIndicator}
                style={{
                  animationDuration: `${notification.duration}ms`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
